/**
 * ProductClickMixin
 *
 * This mixin helps handle clicks on products; allowing the sending of analytics, opening new tabs
 * redirects or overlays to product pages.
 *
 * The simplest way to get started is to include the mixin in your baustein component and implement
 * the function 'getClickOverlayClasses' which should return the classes which link to your product.
 * You also need to include 'overlayEnabled', 'productSlug' and 'reason' options if you want overlays.
 *
 * ```
 *   // ...
 *   mixins: [productClickMixin],
 *
 *   defaultOptions: {
 *      overlayEnabled: false,
 *      productSlug: null,
 *      reason: null
 *   },
 *
 *   getClickOverlayClasses: function () {
 *      return ['.product-color__link'];
 *   }
 *
 * ```
 *
 * If you want specific classes to redirect to product pages you can implement the function
 * 'getClickRedirectClasses' then any clicks to those classes will not open an overlay but will
 * redirect to the product page.
 *
 * ```
 *   getClickRedirectClasses: function () {
 *      return ['.product-color__link2'];
 *   }
 *
 * ```
 *
 * If you want to send analytics for every product click you can do so by implementing the function
 * 'sendAnalytics'.
 *
 * ```
 *  sendAnalytics: function () {
 *      analytics.event('navigation', 'color-picker');
 *  }
 *
 * ```
 *
 */
import _isFunction from "lodash/isFunction";
import browser from "web/script/utils/browser";
import environment from "web/script/modules/environment";
import storage from "web/script/utils/storage";
import globals from "web/script/modules/globals";
import history from "web/script/utils/history";
import store from "web/redux/store";
import url from "web/script/utils/url";
import userProfiler from "web/script/modules/userprofiler";
import { openProductCardModal, openGenericModal } from "web/redux/ducks/send-to-app-modal";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";

const IS_RETURN_TO_FEEDS =
    environment.getFeature("uf_upper_funnel_journey") &&
    environment.getFeature("uf_return_to_feeds");

function extractEventCount(state) {
    return state.eventCountTrackerReducer.shouldBlockAll;
}

/**
 * Product click event handler that will either open an overlay, new tab or redirect to product page.
 */
function _onClick(event, link) {
    if (event.target.classList.contains("show-more-button")) {
        return;
    }

    event.preventDefault();
    this.sendAnalytics();

    let overlayEnabledOption = this.options.overlayEnabled;
    let overlayOnMobileOption = this.options.useOverlayOnMobile;
    let overlaySkipShortDescription =
        environment.getFeature("seo_skip_overlay_redirect_to_pdp") &&
        (event.target.classList.contains("product-card__short-description-name") ||
            event.target.classList.contains("product-card__short-description") ||
            event.target.classList.contains("product-card__designer"));

    let overlayEnabled =
        overlayEnabledOption &&
        this.options.productSlug &&
        browser.supportsHistory &&
        !event.metaKey &&
        // ORG-2469: Experiment skip overlay
        !overlaySkipShortDescription &&
        // Feature flag to use overlay on mobile too
        ((overlayOnMobileOption && browser.getViewport() === browser.VIEWPORTS.MOBILE) ||
            (!browser.ios &&
                !browser.android &&
                browser.getViewport() === browser.VIEWPORTS.DESKTOP));

    this.emit("selected");

    const eventCountBlock = extractEventCount(store.getState());
    if (
        browser.ios &&
        !userProfiler.isLoggedIn() &&
        eventCountBlock &&
        environment.get("pageType") === "feed"
    ) {
        store.dispatch(
            openGenericModal({
                productId: this.options.id,
                modalType: "event_counter",
                product: {
                    productUrl: this.options.productUrl,
                    productImageUrl: this.options.productImageUrl,
                },
                paywalledClassName: "product-card",
            })
        );
        return;
    }

    const productFeedsCapture = environment.getFeature("mg_feeds_product_card_android_block");
    const hasBeenBlockedOnce = !!storage.get("hasBeenBlockedProductCard", false, true);

    const shouldBlockGeneralFeed =
        environment.getFeature("mg_block_product_card") &&
        environment.getFeature("mg_block_product_card") !== "no_block";
    const shouldBlockRelatedFeed =
        (environment.getFeature("mg_block_is_pdp_related_product_card") &&
            environment.getFeature("mg_block_is_pdp_related_product_card") !== "no_block") ||
        (environment.getFeature("mg_android_block_is_pdp_related_product_card") &&
            environment.getFeature("mg_android_block_is_pdp_related_product_card") !== "no_block");
    const shouldBlockProductFeeds =
        productFeedsCapture &&
        (productFeedsCapture === "hard_block" ||
            (productFeedsCapture === "soft_block" && !hasBeenBlockedOnce));

    if (
        browser.ios &&
        !userProfiler.isLoggedIn() &&
        (shouldBlockGeneralFeed || shouldBlockRelatedFeed)
    ) {
        store.dispatch(
            openProductCardModal({
                productUrl: this.options.productUrl,
                productImageUrl: this.options.productImageUrl,
                inStock: this.options.inStock,
            })
        );
        return;
    }

    if (
        browser.android &&
        !userProfiler.isLoggedIn() &&
        (shouldBlockRelatedFeed || shouldBlockProductFeeds)
    ) {
        if (shouldBlockProductFeeds) {
            storage.set("hasBeenBlockedProductCard", "true", true);
        }
        const captureType = shouldBlockProductFeeds ? "feeds_product_card" : "related_product";
        store.dispatch(
            openCustomerCaptureOverlay({
                productId: this.options.id,
                captureType,
                next: this.options.productUrl,
            })
        );
        return;
    }

    if (!link) {
        return;
    }

    // if the overlay isn't enabled, and the target is to open a new tab, then bail out here
    if (!overlayEnabled && link.getAttribute("target") === "_blank") {
        // the lead-link click handler also calls `preventDefault` and calls window.open. we don't
        // want to to it twice.
        if (link.getAttribute("is") !== "lead-link") {
            globals.window.open(link);
        }
        return;
    }

    let newTab = _isNewTab(event);

    if (
        !overlayEnabled ||
        newTab ||
        !this.options.reason ||
        browser.getViewport() === browser.VIEWPORTS.MOBILE
    ) {
        _redirectWithReason.call(this, event, link, newTab);
    } else {
        const href = link.getAttribute("product-href") || link.href;

        let queryParams = {
            reason: this.options.reason,
            link_id: this.options.linkId,
        };

        if (IS_RETURN_TO_FEEDS && this.options.fromFeedUrl) {
            queryParams.fromFeedUrl = this.options.fromFeedUrl;
        }

        let url = new URL(href, window.location.href);
        for (let [k, v] of Object.entries(queryParams)) {
            url.searchParams.set(k, v);
        }

        history.pushState(url);

        this.emit("openOverlay", {
            url: href,
            queryParams,
        });
    }
}

/**
 * Product click event handler that will either redirect or open a new tab to product page.
 */
function _onClickRedirect(event, link) {
    if (event.target.classList.contains("show-more-button")) {
        return;
    }

    let newTab = _isNewTab(event);

    this.emit("selected");
    _redirectWithReason.call(this, event, link, newTab);
}

/**
 * Returns true if a new tab should be used for the event
 */
function _isNewTab(event) {
    return (browser.mac && event.metaKey) || (!browser.mac && event.ctrlKey) || event.which === 2;
}

function _redirectWithReason(event, link, newTab) {
    let openNewTab = newTab || false;
    let parsed = url.parse(link.href);

    event.preventDefault();

    if (IS_RETURN_TO_FEEDS && this.options.fromFeedUrl) {
        parsed.searchParams.set("fromFeedUrl", this.options.fromFeedUrl);
    }

    if (this.options.reason) {
        parsed.searchParams.set("reason", this.options.reason);
    }

    if (openNewTab) {
        globals.window.open(url.unparse(parsed), "_blank");
        return;
    }

    globals.window.location = url.unparse(parsed);
}

export default {
    defaultOptions: {
        overlayEnabled: false,
        productUid: null,
        productSlug: null,
        reason: null,
        linkId: null,
    },

    setupEvents(add) {
        if (this.getClickOverlayClasses) {
            this.getClickOverlayClasses().forEach((classObj) =>
                add("click", classObj, _onClick.bind(this))
            );
        }
        if (this.getClickRedirectClasses) {
            this.getClickRedirectClasses().forEach((classObj) =>
                add("click", classObj, _onClickRedirect.bind(this))
            );
        }
    },

    init() {
        let clickMethodName = "getClickOverlayClasses";
        let clickRedirectMethodName = "getClickRedirectClasses";
        if (!_isFunction(this[clickMethodName]) && !_isFunction(this[clickRedirectMethodName])) {
            throw new Error(
                `Missing functions - at least '${clickMethodName}' or '${clickRedirectMethodName}' should be implemented`
            );
        }
    },

    /**
     * Override function in component to send custom analytics on every product click.
     */
    sendAnalytics() {
        // send your analytics
    },
};
